import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PodcastGuest, PodcastGuestInsert } from '@/types/podcastHost';
import { toast } from 'sonner';

const syncGuestToContacts = async (
  brandId: string,
  guest: { name: string; email?: string | null; phone?: string | null }
): Promise<string | null> => {
  if (!guest.email) return null;
  
  const nameParts = guest.name.trim().split(/\s+/);
  const firstName = nameParts[0] || guest.name;
  const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
  
  try {
    const { data: contactId, error } = await supabase.rpc('upsert_os_contact', {
      p_brand_id: brandId,
      p_email: guest.email,
      p_first_name: firstName,
      p_last_name: lastName,
      p_phone: guest.phone || null,
      p_module: ['Podcast Host'],
      p_activity: ['guest'],
      p_organization: null,
      p_title: null,
      p_user_id: null,
    });
    
    if (error) {
      console.error('Error syncing guest to contacts:', error);
      return null;
    }
    
    return contactId;
  } catch (error) {
    console.error('Error syncing guest to contacts:', error);
    return null;
  }
};

export const usePodcastGuests = (brandId?: string) => {
  return useQuery({
    queryKey: ['podcast-guests', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      
      const { data, error } = await supabase
        .from('os_podcast_shows_guests')
        .select('*')
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as unknown as PodcastGuest[];
    },
    enabled: !!brandId,
  });
};

export const usePodcastGuest = (guestId?: string) => {
  return useQuery({
    queryKey: ['podcast-guest', guestId],
    queryFn: async () => {
      if (!guestId) return null;
      
      const { data, error } = await supabase
        .from('os_podcast_shows_guests')
        .select('*')
        .eq('id', guestId)
        .single();

      if (error) throw error;
      return data as unknown as PodcastGuest;
    },
    enabled: !!guestId,
  });
};

export const useCreatePodcastGuest = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (guestData: PodcastGuestInsert) => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      // Sync to contacts first
      const contactId = await syncGuestToContacts(guestData.brand_id, {
        name: guestData.name,
        email: guestData.email,
        phone: guestData.phone,
      });

      const { data, error } = await supabase
        .from('os_podcast_shows_guests')
        .insert({
          ...guestData,
          user_id: user.user.id,
          contact_id: contactId,
        } as any)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as PodcastGuest;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['podcast-guests', data.brand_id] });
      toast.success('Guest added successfully');
    },
    onError: (error) => {
      console.error('Error creating guest:', error);
      toast.error('Failed to add guest');
    },
  });
};

export const useUpdatePodcastGuest = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<PodcastGuest> }) => {
      // Get current guest for brand_id and contact sync
      const { data: currentGuest } = await supabase
        .from('os_podcast_shows_guests')
        .select('brand_id, name, email, phone')
        .eq('id', id)
        .single();
      
      let contactId: string | null = null;
      if (currentGuest) {
        contactId = await syncGuestToContacts(currentGuest.brand_id, {
          name: updates.name || currentGuest.name,
          email: updates.email || currentGuest.email,
          phone: updates.phone || currentGuest.phone,
        });
      }
      
      const { data, error } = await supabase
        .from('os_podcast_shows_guests')
        .update({ ...updates, ...(contactId ? { contact_id: contactId } : {}) } as any)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as PodcastGuest;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['podcast-guests', data.brand_id] });
      queryClient.invalidateQueries({ queryKey: ['podcast-guest', data.id] });
      toast.success('Guest updated successfully');
    },
    onError: (error) => {
      console.error('Error updating guest:', error);
      toast.error('Failed to update guest');
    },
  });
};

export const useDeletePodcastGuest = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, brandId }: { id: string; brandId: string }) => {
      const { error } = await supabase
        .from('os_podcast_shows_guests')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return brandId;
    },
    onSuccess: (brandId) => {
      queryClient.invalidateQueries({ queryKey: ['podcast-guests', brandId] });
      toast.success('Guest deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting guest:', error);
      toast.error('Failed to delete guest');
    },
  });
};

export const usePromoteCollaboratorToGuest = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ collaboratorId, brandId }: { collaboratorId: string; brandId: string }) => {
      // First, get the collaborator data
      const { data: collaborator, error: fetchError } = await supabase
        .from('os_collabs_collaborators')
        .select('*')
        .eq('id', collaboratorId)
        .single();

      if (fetchError) throw fetchError;
      if (!collaborator) throw new Error('Collaborator not found');

      // Check if guest already exists with this collaborator_id
      const { data: existingGuest } = await supabase
        .from('os_podcast_shows_guests')
        .select('id')
        .eq('collaborator_id', collaboratorId)
        .single();

      if (existingGuest) {
        throw new Error('This collaborator has already been promoted to a guest');
      }

      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      // Sync to contacts
      const contactId = await syncGuestToContacts(brandId, {
        name: collaborator.name,
        email: collaborator.email,
        phone: collaborator.phone,
      });

      // Create the guest from collaborator data
      const { data, error } = await supabase
        .from('os_podcast_shows_guests')
        .insert({
          brand_id: brandId,
          user_id: user.user.id,
          collaborator_id: collaboratorId,
          name: collaborator.name,
          email: collaborator.email,
          phone: collaborator.phone,
          company: collaborator.company,
          job_title: collaborator.job_title,
          linkedin: collaborator.linkedin,
          twitter_handle: collaborator.twitter_handle,
          website: collaborator.website,
          status: 'prospect',
          source: 'collaborator',
          enrichment_data: collaborator.enrichment_data,
          contact_id: contactId,
        } as any)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as PodcastGuest;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['podcast-guests', data.brand_id] });
      toast.success('Collaborator promoted to podcast guest');
    },
    onError: (error: any) => {
      console.error('Error promoting collaborator:', error);
      toast.error(error.message || 'Failed to promote collaborator');
    },
  });
};
